import Axios, { AxiosInstance } from 'axios';
import merge from 'lodash/merge';
import type { IIVConfig } from '@nab/x-types';
import { EventMgr, IEventMgr } from '../EventMgr';
import { tokenRequestSubscribe } from '../TokenExchanger/TokenRequestEventMgr';
import { TokenExchanger } from '../TokenExchanger/TokenExchanger';
import { IRequestTokenDetails, ITokenConfig } from '../TokenExchanger/IRequestTokenDetails';
import { loadScript } from '../utils/loadScript';
import { GlobalAppLoaderName, timeoutSpan, EventNameRequestToken, TokenExchangeEventMgrName, TokenAttributeOnMiniApp } from './constants';
import IAppMetadata, { IAppProperties } from './IAppMetadata';
import { IAppLoader, IAppLoaderOptions } from './IAppLoader';
import ILoadedApp from './ILoadedApp';
import { sdkLoggerInterface as log } from '../Logger/Logger';
import { AnalyticsMgr, DigitalDataStrategy } from '../AnalyticsMgr';
import { IVMgr, GBDManager, IDataSecurity, BCManager, IDataSecurityConfig, IDataSecurityConfigData } from '../DataSecurityMgr';

/**
 * [[include:packages/consumer/sdk-browser/src/AppLoader/README.md]].
 */
export class AppLoader implements IAppLoader {
    /**
     * loaded Apps cache object to hold the apps that have been already loaded
     * Holds the details like {
     *  manifest
     *  app Token
     *  config
     * }
     */
    private loadedApp: { [key: string]: ILoadedApp } = {};

    /**
     * Used to hold the individual instance created for Axios,
     * so that any issues with default instance does not affect this call.
     */
    private httpClientInstance: AxiosInstance = null;

    /**
     * Local variable to hold the seed Token property data
     */
    private seedToken?: string = '';

    private dataSecurityCacheObj: { [key: string]: IDataSecurity } = {};

    private globalEventManager: IEventMgr = new EventMgr(TokenExchangeEventMgrName, [], []);

    constructor() {
        // create a separate instance of axios so that it is not affected by any issues on the default Axios instance. #DIGF1-741
        this.httpClientInstance = Axios.create({ timeout: timeoutSpan });
    }

    /**
     * Get the cached seedToken
     */
    public get SeedToken(): string {
        return this.seedToken;
    }

    /**
     * set the seed token and makes sure
     * that the cached app tokens are cleared if the seed token has been changed
     */
    public set SeedToken(newToken: string) {
        // Clear the mini-app loaded cache.
        Object.keys(this.loadedApp).forEach((app) => {
            if (app && this.loadedApp[app] && this.seedToken !== newToken) {
                const { tokenConfig } = this.loadedApp[app];
                this.loadedApp[app].token = null;
                if (tokenConfig) {
                    tokenConfig.seedToken = newToken;
                }
            }
        });
        this.seedToken = newToken;
    }

    /**
     * Default app loader options
     */
    private defaultOptions: IAppLoaderOptions = {
        appMetadata: null,
        container: document.body,
        tokenConfig: null,
        renderTag: true,
        hooks: {
            onSuccess: null,
            onFailure: null,
            seedTokenProvider: null
        }
    };

    /**
     * Generates the name for the request Token event specific to the app being loaded
     */
    private requestTokenEventName = (tagName: string): string => {
        return `${EventNameRequestToken}:${tagName}`;
    };

    /**
     * The Cached token function returns all the tokens cached for the mini-app and the seed (or Shell) token.
     * This function is required mainly for keep alive functionality that will loop through the token array returned
     * and pass it to the heart beat functionality that can keep it alive for an non idle session.
     */
    public getCachedTokens = (): string[] => {
        const miniAppTokens = Object.entries(this.loadedApp)
            .map<string>(([key, value]) => value && value.token)
            .filter((value) => !!value);
        return [this.SeedToken, ...miniAppTokens];
    };

    /**
     * @deprecated since version 12.0.0; Please use the `loadApp` method from `@nab/x-runtime` to load mini-apps from your shell. Refer the NAB-X {@link https://github.aus.thenational.com/pages/nab-x/nab-x-quickstarts/create/shells/shell-x-runtime.html quickstart} for more info.
     * Loads the NAB-X mini-app configs and returns the tag(string or HTMLElement).
     * @interface IAppLoaderOptions appOptions
     * @returns app tag name
     * @example
     * ```
     * const appOptions = {
     *  appMetadata: {
     *   tag: 'mini-app-example',
     *   manifest: 'https://test.com/asset-manifest.json',
     *   properties: {
     *     appConfig: {
     *      // any object or data that needs to be passed into the mini-apps appConfig.
     *     }
     *   }
     *  }
     * };
     * Apploader.load(appOptions);
     * ```
     */
    public async load(options: IAppLoaderOptions): Promise<string | HTMLElement> {
        // TODO: Change this merge statement to mergeWith();
        const appOptions = merge({}, this.defaultOptions, options); // use the options and default options to generate un-mutated options
        log.debug(`Loading App ${options.appMetadata.tag}`, { ...appOptions });

        let renderTagName: string | HTMLElement = null;

        const { appMetadata, hooks, container, tokenConfig, renderTag } = appOptions;
        const { onSuccess, onFailure, seedTokenProvider } = hooks;

        try {
            // do token config set up.
            log.debug(`setting up token exchange events`, appMetadata);
            this.setUpTokenExchangeEvents(appMetadata);

            log.debug(`checking and initialises default AnalyticsMgr if not pass in`, appMetadata);
            await this.setUpAnalyticsMgr(appMetadata);

            await this.setUpDataSecurityMgr(appMetadata);

            log.debug(`Loading appManifest for`, appMetadata);
            const appTagName = await this.loadAppByManifest(appMetadata, container);
            // cache token configs and set up subscriptions for mini-apps
            this.cacheTokenConfig(tokenConfig, appMetadata, appTagName, seedTokenProvider);

            if (renderTag) {
                log.debug(`Rendering tag on the element appManifest for`, { appMetadata, container });
                renderTagName = this.renderTag(appTagName, container, appMetadata.properties);
            } else {
                renderTagName = appTagName;
            }
            if (onSuccess) {
                onSuccess();
            }

            return renderTagName;
        } catch (e) {
            if (onFailure) {
                onFailure(e);
            }
            // Reset loadedApp, in case any error happens
            const { tag } = appMetadata;
            if (this.loadedApp[tag]) {
                this.loadedApp[tag] = null;
            }

            return undefined;
        }
    }

    /**
     * @param tagName app tag name
     * @param appContainer html container in which app would be rendered
     * @param properties list of properties to be added on to the tag(web component/app).
     * @returns app element reference
     * @example
     */
    public renderTag = (tagName: string, appContainer?: HTMLElement, properties?: IAppProperties<any>) => {
        const container = appContainer || document.body; // this is required if someone explicitly sets the appContainer to null
        const appElement: HTMLElement = document.createElement(tagName);
        if (properties) {
            // We loop through the passed attributes and set them to the HTMLElement as properties.
            // This will help the LitElement read the these properties and pass it on to the mini-app, via the custom elements.
            // NOTE: This line assumes the mini-app was created using LitElement wrapper for the custom element, as in the @nab/nab-x-react package
            Object.entries(properties).forEach(([key, value]) => {
                appElement.setAttribute(key, value && typeof value === 'object' ? JSON.stringify(value) : value);

                // this line is introduced for backward compatibility of an issue in @nab/nab-x-react v3.0.0
                // we require this to be in until we have an confirmation that all the mini-app have upgraded to @nab/nab-x-react v3.0.0
                appElement[key] = value;
            });
        }
        container.appendChild(appElement);

        // Don't request the token if the initial token is already provided when rendering the app,
        // or the mini-app prefers to use the authenticate callback function and wants to skip this default token call.
        if (!properties || (!properties[TokenAttributeOnMiniApp] && !properties.skipAuthOnRender)) {
            this.requestToken(tagName, appElement, false);
        }

        return appElement;
    };

    /**
     * Helps trigger the event that will start a new token fetch/refresh call and update mini-apps custom element with the auth token attribute
     *
     * @deprecated since version 12.0.0; Please use the `authenticate` method from `@nab/x-runtime` to obtain a token
     * @param tagName Name of the Mini-app tag that requires the token
     * @param renderRoot The Web component, DOM reference on which the auth token attribute needs to be
     */
    public requestToken(tagName: string, renderRoot: HTMLElement, forceRefresh: boolean = true) {
        log.debug(`Triggering the Token request event for ${tagName}`, { tagName, renderRoot });
        const { tokenConfig, seedTokenProvider } = this.getConfig(tagName) || {};
        if (tokenConfig && tagName && renderRoot) {
            tokenConfig.refreshToken = forceRefresh;
            this.globalEventManager.publish<IRequestTokenDetails>(this.requestTokenEventName(tagName), {
                renderRoot,
                tagName,
                tokenConfig,
                seedTokenProvider
            });
            return true;
        }
        log.warn(`Missing required params for requestToken: ${tagName}`);
        /* istanbul ignore next */
        if (!tokenConfig) {
            log.info(`TokenConfig is missing or not provided: ${tagName}`);
        }
        return false;
    }

    /**
     *
     * @param tag - App web component tag name
     * @returns App config from App Loader cache
     * @example
     * ```
     * const appConfig = AppLoader.getConfig('test-app');
     * ```
     */
    public getConfig(tag: string) {
        return this.loadedApp && this.loadedApp[tag];
    }

    private async loadAppByManifest(config: IAppMetadata, container: HTMLElement) {
        const { manifest, tag, name, entryPoint = 'main.js' } = config;

        if (!manifest) {
            throw new Error('manifest not provided');
        }

        // If the app has been loaded once, we can use the modules directly
        if (this.loadedApp[tag]) {
            return tag;
        }

        const res = await this.httpClientInstance.get(manifest);
        // Allowing for later version of CRA having a list of files...
        // Warning: this may change again as CRA evolves
        const manifestData = res.data.files ? res.data.files : res.data;

        /**
         * Get entryPoint from manifest, this is required, since the entryPoint will point to the entry file.
         * we expect this entry point to be built with webpack module bundler that will load any or all
         * dependencies within webpack module bundler
         */
        const mainjsPath = manifestData[entryPoint];
        if (!mainjsPath) {
            throw new Error(`${entryPoint} not found in manifest: ${config.manifest}`);
        }

        // Add to loaded App first, in case app needs loading config to prepare
        this.loadedApp[tag] = { ...this.loadedApp[tag], assets: manifestData, ...config };
        if (name) {
            this.loadedApp[name] = this.loadedApp[tag];
        }

        // Get host path
        const hostPath = manifest.slice(0, manifest.lastIndexOf('/'));

        // Get main script path
        const scriptPath = `${hostPath}${mainjsPath}`;

        await loadScript(scriptPath, container);

        return tag;
    }

    /**
     * Checks and loads a default Analytics manager if not supplied.
     */
    private setUpAnalyticsMgr = async (appMetadata: IAppMetadata): Promise<void> => {
        /**
         * Construct build the Analytics Manager from the default strategy,
         * if the Analytics manager instance was not pass through.
         */
        const { properties = {} } = appMetadata;
        let { analyticsMgr } = properties;
        if (analyticsMgr) {
            await analyticsMgr.updatePage({
                pageName: window.document.title,
                site: window.location.href
            });
        } else {
            const defaultDigitalDataStrategy = new DigitalDataStrategy();
            analyticsMgr = new AnalyticsMgr(defaultDigitalDataStrategy);
            await analyticsMgr.initPage({
                pageName: window.document.title,
                site: window.location.href
            });
            // eslint-disable-next-line no-param-reassign
            appMetadata.properties = merge({}, properties, { analyticsMgr });
        }
    };

    /**
     * setUpDataSecurityMgr this sets up the below DataSecurityLibs when the Libraries have been setup to cache them.
     * we always set the getData object, this is for the mini-app to handle the error if it is trying to use a specific getData function and if the library and default config has not been set.
     */
    private setUpDataSecurityMgr = async (appMetadata: IAppMetadata): Promise<void> => {
        const { properties } = appMetadata; // removed default value of empty object [] because the analytics manager creates a properties prior to this function being called.
        let { dataSecurity = {} as IDataSecurityConfig } = properties;
        const {
            bc = {} as IDataSecurityConfigData<string>,
            gbd = {} as IDataSecurityConfigData<string>,
            iv = {} as IDataSecurityConfigData<IIVConfig>
        } = dataSecurity;
        let { gbdObj, ivObj, bcObj } = this.dataSecurityCacheObj;
        /**
         * Initializing the Data security library, irrespective of the library  Url sent.
         * The Security Manager classes should be able to handle the errors and provide the mini-app with appropriate responses.
         */
        if (!bcObj) {
            const { LibraryUrl, configValue } = bc;
            this.dataSecurityCacheObj.bcObj = new BCManager(LibraryUrl, configValue);
            bcObj = this.dataSecurityCacheObj.bcObj;
        }
        (bc as IDataSecurity).getData = bcObj.getData;
        (bc as IDataSecurity).setContext = bcObj.setContext;

        if (!gbdObj) {
            const { LibraryUrl, configValue } = gbd;
            this.dataSecurityCacheObj.gbdObj = new GBDManager(LibraryUrl, configValue);
            gbdObj = this.dataSecurityCacheObj.gbdObj;
        }
        (gbd as IDataSecurity).getData = gbdObj.getData;
        (gbd as IDataSecurity).setContext = gbdObj.setContext;

        if (!ivObj) {
            const { LibraryUrl, configValue } = iv;
            this.dataSecurityCacheObj.ivObj = new IVMgr(LibraryUrl, configValue);
            ivObj = this.dataSecurityCacheObj.ivObj;
        }
        (iv as IDataSecurity).getData = ivObj.getData;
        (iv as IDataSecurity).setContext = ivObj.setContext;

        dataSecurity = merge(dataSecurity, { bc, iv, gbd });

        // merge the settings back to the properties after initialising or setting the getData function
        // eslint-disable-next-line no-param-reassign
        appMetadata.properties = merge({}, properties, { dataSecurity });
    };

    private setUpTokenExchangeEvents(appMetadata): void {
        /**
         * Construct an common event name for request token per app but adding the app tag name.
         * Then add the request token event name to subscribe and publish list
         */
        const { allowedSubscribeEvents, allowedPublishEvents } = this.globalEventManager as any;
        const tokenEventName = this.requestTokenEventName(appMetadata.tag);
        if (allowedSubscribeEvents && !(allowedSubscribeEvents as Array<string>).find((eventName) => eventName === tokenEventName)) {
            (this.globalEventManager as any).allowedSubscribeEvents.push(tokenEventName);
            log.debug(`Added the Subscribe event`, tokenEventName);
        }

        if (allowedPublishEvents && !(allowedPublishEvents as Array<string>).find((eventName) => eventName === tokenEventName)) {
            (this.globalEventManager as any).allowedPublishEvents.push(tokenEventName);
            log.debug(`Added the Publish event`, tokenEventName);
        }
    }

    private cacheTokenConfig(tokenConfig: ITokenConfig, appMetadata: IAppMetadata, appTagName: string, seedTokenProvider): void {
        // loading the manifest will cache the files for the tagName and then we can add the tokenConfig
        if (tokenConfig) {
            this.SeedToken = tokenConfig.seedToken || '';
            this.loadedApp[appMetadata.tag].tokenConfig = tokenConfig;
            // Cache the seed token provider otherwise set it to null.
            this.loadedApp[appMetadata.tag].seedTokenProvider = seedTokenProvider || null;
        }

        // loading the manifest will cache the files for the tagName and then we can add the event
        if (this.loadedApp[appTagName] && !this.loadedApp[appTagName].isTokenEventSubscribed) {
            this.globalEventManager.subscribe(this.requestTokenEventName(appTagName), tokenRequestSubscribe);
            this.loadedApp[appTagName].isTokenEventSubscribed = true;
        }

        // properties will always be introduced by data security and analytics,
        // so it is safe to assume we will always get an instance.
        const { properties } = appMetadata;
        let { authenticate } = properties;

        if (!authenticate) {
            authenticate = this.authenticate;
            // eslint-disable-next-line no-param-reassign
            appMetadata.properties = merge({}, properties, { authenticate });
        }
    }

    private authenticate = async (tagName: string, forceRefresh: boolean = true): Promise<string> => {
        try {
            const cachedConfig = (this.getConfig(tagName) || {}) as ILoadedApp;
            const { tokenConfig, seedTokenProvider } = cachedConfig;

            if (tokenConfig) {
                tokenConfig.refreshToken = forceRefresh;
                const refreshedToken = await TokenExchanger.generateMiniAppToken(tagName, tokenConfig, seedTokenProvider);
                cachedConfig.token = refreshedToken; // update the new refreshed token in cache
                return refreshedToken;
            }
            return Promise.reject(new Error(`Missing required params for requestToken: ${tagName}`));
        } catch (e) {
            return Promise.reject(e);
        }
    };
}

// It's important to keep this as a singleton. As we need to know all the loaded tags.
// Or browser will throw an error if define the custom element again.
// eslint-disable-next-line
function initialAppLoader(): IAppLoader {
    let loader: IAppLoader;

    if (typeof window !== 'undefined') {
        loader = window[GlobalAppLoaderName];
        if (!loader) {
            loader = new AppLoader();
            window[GlobalAppLoaderName] = loader;
        }
    }
    return loader;
}

export default initialAppLoader();
